import { useEffect, useState, useRef } from "react";
import Select from "react-select";
import styled from "styled-components";
// Tiptap components
import { useEditor, EditorContent } from "@tiptap/react";
import StarterKit from "@tiptap/starter-kit";
import TextStyle from "@tiptap/extension-text-style";
import Underline from "@tiptap/extension-underline";
import Color from "@tiptap/extension-color";
import Link from "@tiptap/extension-link";
import ListItem from "@tiptap/extension-list-item";
import BulletList from "@tiptap/extension-bullet-list";
import OrderedList from "@tiptap/extension-ordered-list";
import Heading from "@tiptap/extension-heading";
import FontFamily from "@tiptap/extension-font-family";
import FontSize from "@tiptap/extension-text-style";
import { MergeCodeBlock } from './Extension/MergeCodeBlock';
// MUI components
import Box from "@mui/material/Box";
import FormatBoldIcon from '@mui/icons-material/FormatBold';
import FormatItalicIcon from '@mui/icons-material/FormatItalic';
import FormatUnderlinedIcon from '@mui/icons-material/FormatUnderlined';
import StrikethroughSIcon from '@mui/icons-material/StrikethroughS';
import FormatListNumberedIcon from '@mui/icons-material/FormatListNumbered';
import FormatListBulletedIcon from '@mui/icons-material/FormatListBulleted';
import CodeIcon from '@mui/icons-material/Code';
import { HEADING_OPTIONS } from "./constants";
import "./TiptapEditor.scss";

const FormSelect = styled(Select)`
  display: inline-block;
  padding: 0;
  border-radius: 4px;
  min-width: 150px;
  width: 100%;
  align-content: center;
  font-family: "Poppins";
  font-weight: 400;
  font-size: 13px;
  letter-spacing: 0.005em;
  color: #3d3d3d;
  > div {
    margin-left: 0;
    background: #ffffff;
    border: 1px solid rgba(0, 0, 0, 0.23);
  }
  > div > div:nth-child(2) > span {
    display: none;
  }
`;

const getToolbarStatus = (status = false) => {
  return {
    bold: status,
    italic: status,
    underline: status,
    strike: status,
    orderedList: status,
    unorderedList: status,
    codeBlock: status,
  };
}

const TiptapEditor = ({
  handleChange,
  name = "",
  value = "",
  isDisabled = false,
  placeholder,
  handleBlur,
  handleKeyUp,
}) => {
  const [selectedHeading, setSelectedHeading] = useState(HEADING_OPTIONS[0]);
  const [isHeadingSelected, setIsHeadingSelected] = useState(false);
  const [activeFormats, setActiveFormats] = useState(getToolbarStatus(false));
  const lastHtmlRef = useRef(value);
  const skipBlurRef = useRef(false);
  const toolbarRef = useRef(null);

  const editor = useEditor({
    extensions: [
      StarterKit,
      TextStyle,
      Underline,
      Color,
      FontSize,
      FontFamily.configure({
        types: ["textStyle"],
      }),
      Link.configure({
        openOnClick: true,
      }),
      MergeCodeBlock,
      BulletList,
      OrderedList,
      ListItem,
      Heading.configure({ levels: [1, 2, 3, 4, 5, 6] }),
    ],
    editable: !isDisabled,
    content: value,
    autofocus: 'end', 
    onUpdate: ({ editor }) => {
      if (!isDisabled) {
        // Only update if the content has actually changed
        const newContent = editor.getHTML();
        if (newContent !== lastHtmlRef.current) {
          handleChange(name, newContent);
          lastHtmlRef.current = newContent;
        }
      }
    },
    onSelectionUpdate: ({ editor }) => {
      setActiveFormats({
        bold: editor.isActive("bold"),
        italic: editor.isActive("italic"),
        underline: editor.isActive("underline"),
        strike: editor.isActive("strike"),
        orderedList: editor.isActive("orderedList"),
        unorderedList: editor.isActive("bulletList"),
        codeBlock: editor.isActive("codeBlock"),
      });
      const headingLevel = [1, 2, 3, 4, 5, 6].find((lvl) =>
        editor.isActive("heading", { level: lvl })
      );
      if (headingLevel) {
        const selected = HEADING_OPTIONS.find((opt) => parseInt(opt.value) === headingLevel);
        setSelectedHeading(selected);
        setIsHeadingSelected(true);
      } else {
        setSelectedHeading(HEADING_OPTIONS[0]);
        setIsHeadingSelected(false);
      }
    },
  });

  useEffect(() => {
    if (editor) {
      editor.setEditable(!isDisabled);
    }
  }, [isDisabled, editor]);

  useEffect(() => {
    if (editor && value !== lastHtmlRef.current && value !== editor.getHTML()) {
      // Only update if the value has changed and doesn't match the editor content
      editor.commands.setContent(value, false); // false = don't emit update
      lastHtmlRef.current = value;
    }

    // Event Listeners
    const editorDOM = editor.view.dom;

    const handleDocumentMouseDown = (e) => {
      const clickedToolbar = toolbarRef.current?.contains(e.target);
      skipBlurRef.current = clickedToolbar;
    
      if (clickedToolbar) {
        setTimeout(() => {
          skipBlurRef.current = false;
        }, 0);
      }
    };

    const handleEditorBlur = (e) => {
      if (!skipBlurRef.current && handleBlur) {
        handleBlur(e);
      }
      skipBlurRef.current = false;
    };

    document.addEventListener("mousedown", handleDocumentMouseDown);
    editorDOM.addEventListener("blur", handleEditorBlur);
    editorDOM.addEventListener("keyup", handleKeyUp);

    return () => {
      document.removeEventListener("mousedown", handleDocumentMouseDown);
      editorDOM.removeEventListener("blur", handleEditorBlur);
      editorDOM.removeEventListener("keyup", handleKeyUp);
    };
  }, [value, editor, handleBlur, handleKeyUp]);

  const handleHeadingChange = (e) => {
    const level = parseInt(e.value, 10);
    setIsHeadingSelected(true);
    if (level) {
      editor.chain().focus().setParagraph().setHeading({ level }).run();
    } else {
      editor.chain().focus().setParagraph().run(); // Set normal text
    }
    setSelectedHeading(e);
  };

  const handleCommonToolbarToogle = (type) => {
    skipBlurRef.current = true;
    if (type === "bold") {
      editor.chain().focus().toggleBold().run();
      setActiveFormats((prev) => ({ ...prev, bold: !prev.bold }));
    } else if (type === "italic") {
      editor.chain().focus().toggleItalic().run();
      setActiveFormats((prev) => ({ ...prev, italic: !prev.italic }));
    } else if (type === "underline") {
      editor.chain().focus().toggleUnderline().run();
      setActiveFormats((prev) => ({ ...prev, underline: !prev.underline }));
    } else if (type === "strike") {
      editor.chain().focus().toggleStrike().run();
      setActiveFormats((prev) => ({ ...prev, strike: !prev.strike }));
    } else if (type === "orderedList") {
      editor.chain().focus().toggleOrderedList().run();
      setActiveFormats((prev) => ({ ...prev, orderedList: !prev.orderedList }));
    } else if (type === "unorderedList") {
      editor.chain().focus().toggleBulletList().run();
      setActiveFormats((prev) => ({ ...prev, unorderedList: !prev.unorderedList }));
    }
    else if(type === "codeBlock") {
      editor.chain().focus().mergeCodeBlock().run();
      setActiveFormats((prev) => ({ ...prev, codeBlock: !prev.codeBlock }));
    }
  };

  if (!editor) return null;

  return (
    <Box className="common-tip-tap-main-container">
      {/* Toolbar Content */}
      <Box className="toolbar-main-container" ref={toolbarRef}>
        <Box>
          <FormSelect
            options={HEADING_OPTIONS || []}
            placeholder=""
            onChange={handleHeadingChange}
            isDisabled={isDisabled}
            value={selectedHeading || HEADING_OPTIONS[0]}
          />
        </Box>
        <Box display="flex" alignItems="center" gap="4px">
          <Box>
            <FormatBoldIcon
              onClick={() => handleCommonToolbarToogle("bold")}
              className={`toolbar-icon ${isDisabled ? 'disabled' : ''} ${activeFormats?.bold ? 'active-toolbar' : ''}`}
            />
          </Box>
          <Box>
            <FormatItalicIcon
              onClick={() => handleCommonToolbarToogle("italic")}
              className={`toolbar-icon ${isDisabled ? 'disabled' : ''} ${activeFormats?.italic ? 'active-toolbar' : ''}`}
            />
          </Box>
          <Box>
            <FormatUnderlinedIcon
              onClick={() => handleCommonToolbarToogle("underline")}
              className={`toolbar-icon ${isDisabled ? 'disabled' : ''} ${activeFormats?.underline ? 'active-toolbar' : ''}`}
            />
          </Box>
          <Box>
            <StrikethroughSIcon
              onClick={() => handleCommonToolbarToogle("strike")}
              className={`toolbar-icon ${isDisabled ? 'disabled' : ''} ${activeFormats?.strike ? 'active-toolbar' : ''}`}
            />
          </Box>
        </Box>
        <Box display="flex" alignItems="center" gap="4px">
          <Box>
            <FormatListNumberedIcon
              onClick={() => handleCommonToolbarToogle("orderedList")}
              className={`toolbar-icon ${isDisabled ? 'disabled' : ''} ${activeFormats?.orderedList ? "active-toolbar" : ""}`}
            />
          </Box>
          <Box>
            <FormatListBulletedIcon
              onClick={() => handleCommonToolbarToogle("unorderedList")}
              className={`toolbar-icon ${isDisabled ? 'disabled' : ''} ${activeFormats?.unorderedList ? "active-toolbar" : ""}`}
            />
          </Box>
        </Box>
        <Box height="23px">
          <CodeIcon
            onClick={() => handleCommonToolbarToogle("codeBlock")}
            className={`toolbar-icon ${isDisabled ? "disabled" : ""} ${activeFormats?.codeBlock ? "active-toolbar" : ""}`}
          />
        </Box>
      </Box>

      {/* Editor Content */}
      <Box className="tiptap-main-editor">
        <EditorContent editor={editor} placeholder={placeholder || "Write here..."} />
      </Box>
    </Box>
  );
};

export default TiptapEditor;